import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { ISuperficialAddressProps } from "interfaces/Address";

import { ISubClientsProps } from "interfaces/SubClient";
import { ISuperficialIVehicleProps } from "interfaces/Vehicle";
import { getSubClient } from "services/SubClientServices";

type FormError = { [key: string]: string };

type ContextProviderProps = {
  children: ReactNode;
};

type ContextData = {
  subClient: ISubClientsProps;
  setSubClient: React.Dispatch<React.SetStateAction<ISubClientsProps>>;
  errors: FormError;
  setErrors: React.Dispatch<React.SetStateAction<FormError>>;
  addError: (field: string, message: string) => void;
  removeError: (field: string) => void;
  retrieveSubClient: (id: string) => void;
  resetSubClient: () => void;
  searching: boolean;
  vehicle: ISuperficialIVehicleProps;
  setVehicle: React.Dispatch<React.SetStateAction<ISuperficialIVehicleProps>>;
  address: ISuperficialAddressProps;
  setAddress: React.Dispatch<React.SetStateAction<ISuperficialAddressProps>>;
  coordY: number;
  coordX: number;
  setCoordX: React.Dispatch<React.SetStateAction<number>>;
  setCoordY: React.Dispatch<React.SetStateAction<number>>;
  setEstablishmentName: React.Dispatch<React.SetStateAction<string | undefined>>;
  establishmentName: string | undefined;
};

export const SubClientContext = createContext({} as ContextData);

export function SubClientRegisterProvider({
  children,
}: ContextProviderProps): JSX.Element {
  const [subClient, setSubClient] = useState<ISubClientsProps>(
    {} as ISubClientsProps
  );
  const [vehicle, setVehicle] = useState<ISuperficialIVehicleProps>(
    {} as ISuperficialIVehicleProps
  );

  const [coordY, setCoordY] = useState(0);
  const [coordX, setCoordX] = useState(0);
  const [establishmentName, setEstablishmentName] = useState<string>();

  const [errors, setErrors] = useState<FormError>({});
  const [searching, setSearching] = useState<boolean>(false);
  const [address, setAddress] = useState<ISuperficialAddressProps>(
    {} as ISuperficialAddressProps
  );

  async function retrieveSubClient(id: string) {
    setSearching(true);
    await getSubClient(id)
      .then((res) => {
        const _subClient: ISubClientsProps = {
          clientId: res.item.client.id,
          name: res.item.name,
          superficialAddress: res.item.superficialAddress,
        };
        setSubClient(_subClient);
        setSearching(false);
      })
      .catch((err) => {
        console.log(err);
        setSearching(false);
      });
  }

  function resetSubClient() {
    setSearching(true);
    setSubClient({} as ISubClientsProps);
    setVehicle({} as ISuperficialIVehicleProps);

    setSearching(false);
  }

  function addError(field: string, message: string) {
    setErrors((prev) => {
      return {
        ...prev,
        [field]: message
      };
    });
  }

  function removeError(field: string) {
    setErrors((prev) => {
      delete prev[field]

      return prev
    });
  }

  return (
    <SubClientContext.Provider
      value={{
        subClient,
        setSubClient,
        errors,
        setErrors,
        addError,
        removeError,
        retrieveSubClient,
        resetSubClient,
        searching,
        vehicle,
        setVehicle,
        address,
        setAddress,
        coordY,
        coordX,
        setCoordX,
        setCoordY,
        establishmentName,
        setEstablishmentName,
      }}
    >
      {children}
    </SubClientContext.Provider>
  );
}

export const useSubClientRegister = () => {
  return useContext(SubClientContext);
};
